import { inject, Injectable } from '@angular/core';
import {
  CreateSectionInput,
  FieldConfig,
  Form,
  FormNode,
  Section,
  UpdateSectionInput
} from '@app/core/model/other/field-config';
import { removeTypenameProperty } from '@app/shared/extra/utils';
import { TranslateService } from '@ngx-translate/core';
import ApiService from '@services/api.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { IRowNode } from 'ag-grid-community';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { DocumentNode } from 'graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SectionService {
  public readonly sectionGraphqlFragment: DocumentNode;

  private appManager: AppManager = inject(AppManager);
  private accessManager = inject(AccessManager);
  private translate: TranslateService = inject(TranslateService);
  private apiService = inject(ApiService);

  constructor() {
    this.sectionGraphqlFragment = gql`
      fragment SectionInfo on Section {
        id
        organizationId
        code
        formCode
        order
        customOptions
        label
        entityType
        emptyLabel
        assetTypesList {
          id
          name
        }
        allAssetTypes
        fieldGroupsList {
          code
          order
          label
          emptyLabel
          tooltip
          fieldGroupType
          customOptions
          fieldConfigsList {
            fieldCode
            order
            customOptions
            field {
              code
              label
              tooltip
              entityType
              fieldType
              checkType
              computed
              parentPathList
              fieldValuesList
              validatorsList {
                conditionsList {
                  field
                  operator
                  value
                }
                definition
                type
              }
            }
            conditionsToViewList {
              field
              operator
              value
            }
            conditionsToEditList {
              field
              operator
              value
            }
          }
          conditionsToViewList {
            field
            operator
            value
          }
          conditionsToEditList {
            field
            operator
            value
          }
        }
        conditionsToViewList {
          field
          operator
          value
        }
        conditionsToEditList {
          field
          operator
          value
        }
      }
    `;
  }

  /**
   * Iterate through the section's required fields and return all the fields without a write access.
   * @param section - The section object that contains fields and their configurations.
   * @return List of fields without write permissions, or an empty list if none are found.
   */
  public missingRequiredFields(section: Section): FieldConfig[] {
    return section.children.flatMap(fieldGroup => fieldGroup.children)
      .filter(field =>
          field.customOptions.required && !this.accessManager.hasFieldWriteAccess(
            field.field.alias,
            field.fieldEntityType
          )
      );
  }

  /**
   * Get all Sections by form code.
   * @param formCode Form code.
   * @return Observable that emits a list of Sections.
   */
  public getSectionsByFormCode(formCode: string): Observable<Section[]> {
    const query = gql`
      query FormSections($formCode: String!, $orgId: String!) {
        sectionsByForm(formCode: $formCode, organizationId: $orgId) {
          ...SectionInfo
        }
      }
      ${this.sectionGraphqlFragment}
    `;
    const variables = {
      formCode,
      orgId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Section, data['sectionsByForm'] as Section[])));
  }

  /**
   * Get all Forms of an organization
   * @return Observable that emits a list of forms.
   */
  public getForms(): Observable<Form[]> {
    const query = gql`
      query Forms($orgId: String!) {
        forms(organizationId: $orgId) {
          code
          entityType
        }
      }
    `;
    const variables = {
      orgId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => data['forms'] as Form[]));
  }

  /**
   * Get all Sections of an organization.
   * The sections are then grouped by their form codes and flattened to calculate the data path for Ag-Grid tree
   * @return Observable that emits a list of FormNodes.
   */
  public getAllFlattenedFormNodes(): Observable<FormNode[]> {
    const query = gql`
      query AllSections($orgId: String!) {
        sections(organizationId: $orgId) {
          ...SectionInfo
        }
      }
      ${this.sectionGraphqlFragment}
    `;
    const variables = {
      orgId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(
        map(data => plainToInstance(Section, data['sections'] as Section[])),
        map(sections => this.groupByFormCode(sections)), // Add a super level around the sections
        map(forms => this.flattenChildrenRecursively(forms))
      );
  }

  /**
   * Creates a section given an input containing information about the section itself
   * such as the module it belongs to, its label and the order in which it needs to appear in the form
   * @param sectionInput Input containing information about the section
   * @return Observable containing the newly created section among all sections in the form
   */
  public createSection(sectionInput: CreateSectionInput): Observable<FormNode[]> {
    const mutation = gql`
      mutation CreateSection($createSectionInput: CreateSectionInput!) {
        createSection(sectionInput: $createSectionInput) {
          ...SectionInfo
        }
      }
      ${this.sectionGraphqlFragment}
    `;
    const variables = {
      createSectionInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...sectionInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Section, data['createSection'] as Section[])),
        map(sections => this.groupByFormCode(sections)), // Add a super level around the sections
        map(forms => this.flattenChildrenRecursively(forms))
      );
  }

  /**
   * Updates a section given an input of a few properties
   * @param sectionId Id of the section to update
   * @param sectionInput Section data to update
   * @return Observable containing the newly updated section among all sections in the form
   */
  public updateSection(sectionId: string, sectionInput: UpdateSectionInput): Observable<FormNode[]> {
    const mutation = gql`
      mutation UpdateSection($updateSectionInput: UpdateSectionInput!) {
        updateSection(sectionInput: $updateSectionInput) {
          ...SectionInfo
        }
      }
      ${this.sectionGraphqlFragment}
    `;
    const variables = {
      updateSectionInput: {id: sectionId, ...sectionInput}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Section, data['updateSection'] as Section[])),
        map(sections => this.groupByFormCode(sections)), // Add a super level around the sections
        map(forms => this.flattenChildrenRecursively(forms))
      );
  }

  /**
   * Deletes a form node from the section config datagrid
   * @param nodeToDelete the Ag-Grid row node to remove along with its children
   * @return Observable containing response if the operation was successful
   */
  public deleteSectionFormNodes(nodeToDelete: IRowNode): Observable<FormNode[]> {
    const mutation = gql`
      mutation DeleteFormNodes($nodePath: [String]!, $organizationId: String!) {
        deleteSectionFormNodes(organizationId: $organizationId, nodePath: $nodePath) {
          ...SectionInfo
        }
      }
      ${this.sectionGraphqlFragment}
    `;
    const variables = {
      nodePath: nodeToDelete.data.hierarchy,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Section, data['deleteSectionFormNodes'] as Section[])),
        map(sections => this.groupByFormCode(sections)), // Add a super level around the sections
        map(forms => this.flattenChildrenRecursively(forms))
      );
  }

  /**
   * Groups the sections by their form code and creates a super structure around them as a datagrid tree node
   * @param data the list of sections to group
   * @return a list of Forms, superstructure of the sections grouped by their form code
   */
  private groupByFormCode(data: Section[]): Form[] {
    return data.reduce((forms: Form[], section) => {
      let formCode = section.formCode;
      let existingForm = forms.find((s: { code: string }) => s && s.code === formCode);
      if (!existingForm) {
        existingForm = plainToInstance(Form, {
          label: `${this.translate.instant('LABEL.' + section.entityType)} - ${this.translate.instant('LABEL.FORM_' + formCode.toUpperCase())}`,
          code: formCode,
          entityType: section.entityType,
          graphQlTypeName: 'Form',
          customOptions: section.customOptions,
          children: []
        });
        forms.push(existingForm);
      }
      existingForm.children.push(section);
      return forms;
    }, []);
  }

  /**
   * Flattens the children of a form node recursively and creates a list of FormNodes
   * containing the data path from the root FormNode to all children
   * @param data the form nodes to flatten
   * @param parent the parent node, if present
   * @param childHierarchy the hierarchy data structure to build
   * @return a list of FormNodes (forms, sections, fieldGroups, fieldConfigs) containing the hierarchy path from the root FormNode to all children
   */
  private flattenChildrenRecursively(data?: FormNode[], parent = null, childHierarchy = null): FormNode[] {
    let newData = [];
    if (!data) return newData; // Abort early if data is undefined

    data.forEach(initialRow => {
      let parentHierarchy = [];
      initialRow.hierarchy = parentHierarchy;

      if (parent) {
        initialRow['parent'] = parent;
        parentHierarchy = [...childHierarchy];
        initialRow.hierarchy = parentHierarchy;
      }

      parentHierarchy.push(initialRow['code']);
      newData.push(initialRow);
      switch (initialRow['graphQlTypeName']) {
        case 'Form':
        case 'Section':
        case 'FieldGroup':
          newData = [
            ...newData,
            ...this.flattenChildrenRecursively(
              initialRow['children'],
              initialRow,
              parentHierarchy
            )
          ];
          break;
        default:
          break;
      }
    });
    return newData;
  }

  /**
   * Converts a Section object to an UpdateSectionInput object. Only keeps the relevant properties to send downstream.
   * @param section the Section object to convert
   * @return the SectionInput object
   */
  public dataToSectionInput(section: Section): UpdateSectionInput {
    return removeTypenameProperty({
      label: section.label,
      order: section.order,
      allAssetTypes: section.allAssetTypes,
      assetTypeIds: section.assetTypes.map(assetType => assetType.id),
      customOptions: section.customOptions,
      fieldGroups: section.children.map(group => ({
        code: group.code,
        label: group.label,
        order: group.order,
        tooltip: group.tooltip,
        emptyLabel: group.emptyLabel,
        customOptions: group.customOptions,
        fieldGroupType: group.fieldGroupType,
        fieldConfigs: group.children.map(config => ({
          fieldCode: config.fieldCode,
          order: config.order,
          customOptions: config.customOptions,
          conditionsToView: config.conditionsToView,
          conditionsToEdit: config.conditionsToEdit
        })),
        conditionsToView: group.conditionsToView,
        conditionsToEdit: group.conditionsToEdit
      })),
      conditionsToView: section.conditionsToView,
      conditionsToEdit: section.conditionsToEdit
    });
  }
}
